import './App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { I18nProvider } from './i18n';
import { AuthProvider } from './auth/AuthContext';
import { RequireAuth, RequireAdmin } from './auth/RequireAuth';
import RouteTracker from './components/RouteTracker';
import AIAssistant from './components/AIAssistant';
import FeedbackWidget from './components/FeedbackWidget';
import PendingDownloadBanner from './components/PendingDownloadBanner';
import AiCreditsModalHost from './components/AiCreditsModalHost';

// Floating launchers (AI assistant + feedback chat) are only meaningful
// on the landing page. On all other routes the user is focused on a
// concrete tool/workflow and the widgets were getting in the way of
// the configurator UI — especially the bottom-left feedback launcher
// overlapping mobile sidebars. We mount them through this guard so
// they appear ONLY on the exact home route '/'.
function HomeOnlyFloaters() {
  const { pathname } = useLocation();
  if (pathname !== '/') return null;
  return (
    <>
      <AIAssistant />
      <FeedbackWidget />
    </>
  );
}

// Eagerly loaded — small + landing/login critical path
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import { Forgot, Reset, VerifyEmail } from './pages/ForgotReset';

// Lazy-loaded — heavy or non-critical pages
// Each becomes its own chunk; downloaded only when the user navigates there.
const Configurator   = lazy(() => import('./pages/Configurator'));
const Vectorize      = lazy(() => import('./pages/Vectorize'));
const ImagePuzzle    = lazy(() => import('./pages/ImagePuzzle'));
const PhotoDots      = lazy(() => import('./pages/PhotoDots'));
const GearBuilder    = lazy(() => import('./pages/GearBuilder'));
const BendCalculator = lazy(() => import('./pages/BendCalculator'));
const ACMTray        = lazy(() => import('./pages/ACMTray'));
const FacadePanel    = lazy(() => import('./pages/FacadePanel'));
const ChannelLetter  = lazy(() => import('./pages/ChannelLetter'));
const FacadeFrame    = lazy(() => import('./pages/FacadeFrame'));
const PartsLibrary   = lazy(() => import('./pages/PartsLibrary'));
const GeodesicDome   = lazy(() => import('./pages/GeodesicDome'));
const LBracket       = lazy(() => import('./pages/LBracket'));
const Enclosure      = lazy(() => import('./pages/Enclosure'));
const RackPanel      = lazy(() => import('./pages/RackPanel'));
const SliceStack     = lazy(() => import('./pages/SliceStack'));
const FillPatternPanel = lazy(() => import('./pages/FillPatternPanel'));
const ConnectedText  = lazy(() => import('./pages/ConnectedText'));
const SingleLineText = lazy(() => import('./pages/SingleLineText'));
const StirStick      = lazy(() => import('./pages/StirStick'));
const NeonSign       = lazy(() => import('./pages/NeonSign'));
const LogoToNeon     = lazy(() => import('./pages/LogoToNeon'));
const LedPixelBoard  = lazy(() => import('./pages/LedPixelBoard'));
const BoardCase      = lazy(() => import('./pages/BoardCase'));
const AIDxfGen       = lazy(() => import('./pages/AIDxfGen'));
const Polygon3D      = lazy(() => import('./pages/Polygon3D'));
const DisplayGenerator = lazy(() => import('./pages/DisplayGenerator'));
const SpeakerSealed  = lazy(() => import('./pages/SpeakerSealed'));
const WallPocketDisplay = lazy(() => import('./pages/WallPocketDisplay'));
const QRCodeGen      = lazy(() => import('./pages/QRCodeGen'));
const TubularBuilder = lazy(() => import('./pages/TubularBuilder'));
const TubeNesting    = lazy(() => import('./pages/TubeNesting'));

const Account        = lazy(() => import('./pages/Account'));
const Admin          = lazy(() => import('./pages/Admin'));
const Pricing        = lazy(() => import('./pages/Pricing'));
const Privacy        = lazy(() => import('./pages/Privacy'));
const Terms          = lazy(() => import('./pages/Terms'));
const Blog           = lazy(() => import('./pages/Blog'));
const Resources      = lazy(() => import('./pages/Resources'));
const Examples       = lazy(() => import('./pages/Examples'));
const Donate         = lazy(() => import('./pages/Donate'));
const Thanks         = lazy(() => import('./pages/Thanks'));
const ThanksSingle   = lazy(() => import('./pages/ThanksSingle'));
const Influencer     = lazy(() => import('./pages/Influencer'));
const ReleaseNotes   = lazy(() => import('./pages/ReleaseNotes'));
const Learn          = lazy(() => import('./pages/Learn'));
const Vs             = lazy(() => import('./pages/Vs'));

// Lightweight skeleton shown while a chunk loads (typically <300ms).
// Matches the Header height + a neutral background so the layout doesn't
// jump when the real page mounts.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-[#fafafa]">
      <div className="h-14 border-b border-zinc-200 bg-white" />
      <div className="mx-auto max-w-7xl px-4 py-10">
        <div className="h-8 w-48 rounded bg-zinc-200 animate-pulse" />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-6">
          <div className="h-96 rounded-xl bg-zinc-100 animate-pulse" />
          <div className="h-96 rounded-xl bg-zinc-100 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-[#fafafa]">
          <BrowserRouter>
            <RouteTracker />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot" element={<Forgot />} />
                <Route path="/reset" element={<Reset />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/account/*" element={<RequireAuth><Account /></RequireAuth>} />
                <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<Blog />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/examples" element={<Examples />} />
                <Route path="/donate" element={<Donate />} />
                <Route path="/thanks" element={<Thanks />} />
                {/* Landing page for one-off single-project download
                    purchases. Stripe redirects here as
                    /thanks/single?session_id=cs_… The page polls the
                    Worker until the webhook flips status to 'succeeded',
                    then offers the download button. */}
                <Route path="/thanks/single" element={<ThanksSingle />} />
                <Route path="/influencer" element={<Influencer />} />
                <Route path="/release-notes" element={<ReleaseNotes />} />
                <Route path="/learn" element={<Learn />} />
                {/* Competitor comparison landings — SEO long-tail
                    "X vs Y" queries. Renders by slug. */}
                <Route path="/vs/:slug" element={<Vs />} />
                {/* ── Tool routes ───────────────────────────────────
                    EVERY tool is registered TWICE:
                      1. /<slug>-generator (or /<slug> for calculators)
                         — the NEW canonical path, keyword-rich,
                         flat namespace. This is what <Seo canonical>
                         emits and the sitemap lists.
                      2. /box/<slug> — the LEGACY path. Kept working
                         so external backlinks, bookmarks, embedded
                         iframes and our own analytics history don't
                         break. React Router serves the same
                         component, the canonical tag tells Google
                         to consolidate signals on path #1.
                    The legacy alias may be removed safely once the
                    Search Console shows /<slug>-generator has fully
                    replaced /box/<slug> in the index (typically 6–8
                    weeks after launch). */}

                {/* New canonical paths */}
                <Route path="/vectorize-generator" element={<Vectorize />} />
                <Route path="/image-puzzle-generator" element={<ImagePuzzle />} />
                <Route path="/photo-dots-generator" element={<PhotoDots />} />
                <Route path="/gear-generator" element={<GearBuilder />} />
                <Route path="/bend-calculator" element={<BendCalculator />} />
                <Route path="/acm-tray-generator" element={<ACMTray />} />
                <Route path="/facade-panel-generator" element={<FacadePanel />} />
                <Route path="/channel-letter-generator" element={<ChannelLetter />} />
                <Route path="/facade-frame-generator" element={<FacadeFrame />} />
                <Route path="/parts-library-generator" element={<PartsLibrary />} />
                <Route path="/geodesic-dome-generator" element={<GeodesicDome />} />
                <Route path="/l-bracket-generator" element={<LBracket />} />
                <Route path="/enclosure-generator" element={<Enclosure />} />
                <Route path="/rack-panel-generator" element={<RackPanel />} />
                <Route path="/slice-stack-generator" element={<SliceStack />} />
                <Route path="/fill-pattern-generator" element={<FillPatternPanel />} />
                <Route path="/connected-text-generator" element={<ConnectedText />} />
                <Route path="/single-line-text-generator" element={<SingleLineText />} />
                <Route path="/stir-stick-generator" element={<StirStick />} />
                <Route path="/neon-sign-generator" element={<NeonSign />} />
                <Route path="/logo-to-neon-generator" element={<LogoToNeon />} />
                <Route path="/led-pixel-board-generator" element={<LedPixelBoard />} />
                <Route path="/board-case-generator" element={<BoardCase />} />
                <Route path="/ai-dxf-generator" element={<AIDxfGen />} />
                <Route path="/polygon-3d-generator" element={<Polygon3D />} />
                <Route path="/display-generator" element={<DisplayGenerator />} />
                <Route path="/sealed-speaker-generator" element={<SpeakerSealed />} />
                <Route path="/wall-pocket-display" element={<WallPocketDisplay />} />
                <Route path="/qrcode-generator" element={<QRCodeGen />} />
                <Route path="/tube-profile-designer" element={<TubularBuilder />} />
                <Route path="/tube-nesting" element={<TubeNesting />} />
                {/* Configurator-driven tools — listed explicitly (no
                    dynamic catch-all here) so each canonical URL is a
                    real, indexable route. A wildcard /:slug catch-all
                    would shadow /pricing, /blog, /admin etc. and is
                    riskier than the small price of listing 17 lines.
                    The slugOverride prop tells Configurator which
                    catalogue entry to render — the URL itself stays
                    keyword-rich for SEO ("/cubby-shelf-generator")
                    while the internal slug remains the short catalogue
                    key ("cubby"). */}
                <Route path="/basic-box-generator" element={<Configurator slugOverride="basic" />} />
                <Route path="/cardboard-box-generator" element={<Configurator slugOverride="cardboard" />} />
                <Route path="/cubby-shelf-generator" element={<Configurator slugOverride="cubby" />} />
                <Route path="/curved-box-generator" element={<Configurator slugOverride="curved" />} />
                <Route path="/divider-box-generator" element={<Configurator slugOverride="divider" />} />
                <Route path="/grid-box-generator" element={<Configurator slugOverride="grid" />} />
                <Route path="/hinged-box-generator" element={<Configurator slugOverride="hinge" />} />
                <Route path="/kerf-calibration" element={<Configurator slugOverride="kerf" />} />
                <Route path="/laser-focus-test" element={<Configurator slugOverride="focus" />} />
                <Route path="/luminaire-generator" element={<Configurator slugOverride="luminaire" />} />
                <Route path="/luminaire-shapes-generator" element={<Configurator slugOverride="luminaire-shapes" />} />
                <Route path="/maze-generator" element={<Configurator slugOverride="maze" />} />
                <Route path="/decorative-panel-generator" element={<Configurator slugOverride="panel" />} />
                <Route path="/polygon-box-generator" element={<Configurator slugOverride="polygon" />} />
                <Route path="/puzzle-generator" element={<Configurator slugOverride="puzzle" />} />
                <Route path="/sliding-lid-box-generator" element={<Configurator slugOverride="slide" />} />
                <Route path="/ballot-box-generator" element={<Configurator slugOverride="ballot" />} />

                {/* Legacy paths — kept as working aliases. Do NOT add
                    new tools here; register them above only. */}
                <Route path="/box/vectorize" element={<Vectorize />} />
                <Route path="/box/image-puzzle" element={<ImagePuzzle />} />
                <Route path="/box/photo-dots" element={<PhotoDots />} />
                <Route path="/box/gear-builder" element={<GearBuilder />} />
                <Route path="/box/bend-calculator" element={<BendCalculator />} />
                <Route path="/box/l-bracket" element={<LBracket />} />
                <Route path="/box/enclosure" element={<Enclosure />} />
                <Route path="/box/rack-panel" element={<RackPanel />} />
                <Route path="/box/slice-stack" element={<SliceStack />} />
                <Route path="/box/fill-pattern" element={<FillPatternPanel />} />
                <Route path="/box/connected-text" element={<ConnectedText />} />
                <Route path="/box/channel-letter" element={<ChannelLetter />} />
                <Route path="/box/facade-frame" element={<FacadeFrame />} />
                <Route path="/box/parts-library" element={<PartsLibrary />} />
                <Route path="/box/geodesic-dome" element={<GeodesicDome />} />
                <Route path="/box/single-line-text" element={<SingleLineText />} />
                <Route path="/box/stir-stick" element={<StirStick />} />
                <Route path="/box/neon-sign" element={<NeonSign />} />
                <Route path="/box/logo-to-neon" element={<LogoToNeon />} />
                <Route path="/box/led-pixel-board" element={<LedPixelBoard />} />
                <Route path="/box/board-case" element={<BoardCase />} />
                <Route path="/box/ai-dxf-gen" element={<AIDxfGen />} />
                <Route path="/box/polygon-3d" element={<Polygon3D />} />
                <Route path="/box/display" element={<DisplayGenerator />} />
                <Route path="/box/speaker-sealed" element={<SpeakerSealed />} />
                <Route path="/box/wall-pocket" element={<WallPocketDisplay />} />
                <Route path="/box/qrcode" element={<QRCodeGen />} />
                <Route path="/box/tubular-builder" element={<TubularBuilder />} />
                <Route path="/box/:slug" element={<Configurator />} />
              </Routes>
            </Suspense>
            {/* Floating copilot + feedback launcher — restricted to
                the landing page (home '/') only. They were obscuring
                tool sidebars and the configurator preview on every
                other route. */}
            <HomeOnlyFloaters />
            {/* Reminder for in-flight single-file purchases. Reads
                localStorage so it works for anonymous buyers, across
                page navigations, browser restarts, and even direct
                URLs that don't carry a ?session_id. */}
            <PendingDownloadBanner />
            {/* Global host for the "buy AI credits" modal — auto-opens
                whenever any AI route returns 429 ai_quota_exceeded
                (see lib/api.js for the dispatched CustomEvent). One
                mount point covers vectorize/halftone/neon/dxf-gen/
                assistant/image-enhance/… without per-page wiring. */}
            <AiCreditsModalHost />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
